import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addCustomerOdooLink,
  getCustomerDetail,
  getCustomers,
  removeCustomerOdooLink,
  searchOdooPartners,
  updateCustomer,
} from '../api/customers';

export function useCustomers() {
  return useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
    // Invoicing statuses come from the nightly Odoo sync, so an hour is plenty.
    refetchInterval: 3_600_000,
  });
}

/** One customer with its subscriptions, invoices (mail threads, reminder drafts) and timeline. */
export function useCustomerDetail(id: string | null) {
  return useQuery({
    queryKey: ['customers', 'detail', id],
    queryFn: () => getCustomerDetail(id!),
    enabled: id !== null,
    // The mail threads are read live from Odoo on every call; no point hammering it.
    staleTime: 60_000,
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      orderFormUrl?: string | null;
      orderFormSignedAt?: string | null;
      notes?: string | null;
      archived?: boolean;
    }) => updateCustomer(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}

/** Odoo partners matching a typed name, for the link picker. Off until two characters are typed. */
export function useOdooPartnerSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['customers', 'odoo-partners', q],
    queryFn: () => searchOdooPartners(q),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });
}

export function useAddCustomerOdooLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addCustomerOdooLink,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useRemoveCustomerOdooLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeCustomerOdooLink,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}
